import React from 'react';
import AuthImage from '@/components/AuthImage';
import { AiEstimateNotice } from '@/components/AiEstimateNotice';
import { motion } from 'framer-motion';
import { Calendar, StickyNote, Coffee, Utensils, Wine, Check, X, Edit2, Loader2, Sparkles, Clock, AlertCircle, FileText, Microscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DeleteEntryButton } from '@/components/food/DeleteEntryButton';

/**
 * FoodEntryCard - Individual food entry card component
 * Displays a food entry with image, meal type badge, analysis, and action buttons
 */
const FoodEntryCard = ({
  entry,
  index,
  dateIndex,
  editing,
  editedAnalysis,
  setEditedAnalysis,
  finalizing,
  runningAiAnalysis,
  relabelling,
  onEntryClick,
  onEditAnalysis,
  onSaveAnalysis,
  onCancelEdit,
  onFinalizeEntry,
  onRunAiAnalysis,
  onRelabel,
  onUndoFinalize,
  onDeleteEntry,
  undoingFinalization,
  isNearAutoFinalization,
}) => {
  // Get meal type color based on type
  const getMealTypeColor = (type) => {
    const colors = {
      breakfast: 'bg-amber-500/90 text-white',
      lunch: 'bg-primary/90 text-white',
      dinner: 'bg-purple-500/90 text-white',
      snack: 'bg-pink-500/90 text-white',
      drink: 'bg-blue-500/90 text-white',
      late_night: 'bg-indigo-500/90 text-white',
      meal: 'bg-green-500/90 text-white'
    };
    return colors[type] || 'bg-gray-500/90 text-white';
  };

  // Get meal type icon
  const getMealTypeIcon = (type) => {
    const icons = {
      breakfast: <Coffee className="w-3 h-3" />,
      lunch: <Utensils className="w-3 h-3" />,
      dinner: <Utensils className="w-3 h-3" />,
      snack: <Coffee className="w-3 h-3" />,
      drink: <Wine className="w-3 h-3" />,
      late_night: <Utensils className="w-3 h-3" />,
      meal: <Utensils className="w-3 h-3" />
    };
    return icons[type] || <Utensils className="w-3 h-3" />;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="relative flex flex-col rounded-xl overflow-hidden group cursor-pointer bg-card border border-border hover:shadow-lg transition-shadow"
      data-testid={`food-entry-${dateIndex}-${index}`}
      onClick={() => entry.is_finalized && onEntryClick(entry)}
    >
      {/* Image Section */}
      <div className="aspect-square relative">
        {entry.image_url ? (
          <AuthImage
            src={entry.image_url}
            alt={`${entry.meal_type} entry`}
            thumb
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex flex-col items-center justify-center p-4">
            <FileText className="w-12 h-12 text-amber-500 mb-2" />
            <span className="text-xs text-amber-700 font-medium text-center">Text Description</span>
            {entry.text_entry_details && (
              <p className="text-sm text-amber-800 mt-2 text-center line-clamp-2 font-medium">
                {entry.text_entry_details.meal_name || entry.text_entry_details.drink_type || entry.notes}
              </p>
            )}
          </div>
        )}
        
        {/* Delete (owner) - director 13 Oct 2026 */}
        {onDeleteEntry && (
          <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
            <DeleteEntryButton entry={entry} onDeleted={onDeleteEntry} />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
          <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold capitalize shadow-lg ${getMealTypeColor(entry.meal_type)}`}>
            {getMealTypeIcon(entry.meal_type)}
            {entry.meal_type}
          </span>
          {entry.auto_finalized && entry.is_finalized && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-500/90 text-white shadow-lg">
              <Clock className="w-3 h-3" />
              Auto-finalized
            </span>
          )}
          {!entry.is_finalized && entry.analysis && isNearAutoFinalization(entry) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/90 text-white shadow-lg animate-pulse">
              <AlertCircle className="w-3 h-3" />
              Auto-finalizes soon
            </span>
          )}
        </div>
        
        {/* Click for details indicator */}
        {entry.is_finalized && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-black/60 text-white">
              <Microscope className="w-3 h-3" />
              Click for details
            </span>
          </div>
        )}
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
            <div className="flex items-center gap-2 text-white text-sm">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(entry.created_at)}</span>
            </div>
            {entry.notes && (
              <div className="flex items-start gap-2 text-white text-sm">
                <StickyNote className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2">{entry.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Analysis Section */}
      {entry.analysis && entry.analysis !== 'Analysis unavailable' && entry.analysis !== 'None' && entry.analysis !== '' && (
        <div className="p-4 bg-primary/5 border-t border-primary/20">
          <p className="font-bold text-sm text-primary mb-2">AI Analysis:</p>
          
          {editing === entry.id ? (
            <div className="space-y-2">
              <Textarea
                value={editedAnalysis}
                onChange={(e) => setEditedAnalysis(e.target.value)}
                className="text-sm min-h-24"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onSaveAnalysis(entry.id)}
                  className="flex-1"
                >
                  <Check className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCancelEdit}
                  className="flex-1"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line mb-3">{entry.analysis}</p>
              
              {!entry.is_finalized && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onFinalizeEntry(entry.id); }}
                    disabled={finalizing === entry.id}
                    className="flex-1 bg-gradient-to-r from-primary to-[#8FD14F]"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    {finalizing === entry.id ? 'Finalizing...' : 'Confirm & Finalize'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); onEditAnalysis(entry); }}
                    className="flex-1"
                  >
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* No Analysis - Show Run AI Analysis button */}
      {(!entry.analysis || entry.analysis === 'Analysis unavailable' || entry.analysis === 'None' || entry.analysis === '') && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-t border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1" data-testid={`analysis-status-${entry.id}`}>
            <Sparkles className="w-3 h-3" />
            {entry.analysis_error === 'ai_unavailable' ? 'Analysis temporarily unavailable, please try again shortly.' : 'No AI analysis available'}
          </p>
          <Button
            data-testid={`run-ai-analysis-button-${entry.id}`}
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRunAiAnalysis(entry.id); }}
            disabled={runningAiAnalysis === entry.id}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            {runningAiAnalysis === entry.id ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 mr-1" />
                {entry.analysis_error === 'ai_unavailable' ? 'Try again' : 'Run AI Analysis'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Finalized macros summary */}
      {entry.is_finalized && (
        <div className="p-3 bg-muted/30 border-t border-border">
          <div className="flex justify-between items-center text-xs">
            <div className="flex gap-3">
              <span className="text-amber-600 font-medium">{entry.calories || 0} cal</span>
              <span className="text-primary font-medium">{entry.protein_g || 0}g P</span>
              <span className="text-blue-600 font-medium">{entry.carbs_g || 0}g C</span>
              <span className="text-orange-600 font-medium">{entry.fat_g || 0}g F</span>
            </div>
            <AiEstimateNotice />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FoodEntryCard;
